import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  File,
  Image,
  Zap,
  Eye
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ExtendedLead } from './types';

interface Fatura {
  id: string;
  nome_arquivo: string;
  nome_original: string;
  tipo_documento: string;
  tipo_arquivo: string;
  tamanho_bytes: number;
  descricao?: string;
  created_at: string;
}

interface FaturasConsumoProps {
  leadData: ExtendedLead;
  onUpdate: () => void;
}

interface ConsumoData {
  // Campos para Grupo B
  consumo_anual?: number | null;
  despesa_energia_anual?: number | null;
  despesa_cosip_anual?: number | null;
  despesa_fatura_anual?: number | null;
  // Campos para Grupo A e Mercado Livre
  consumo_ponta_anual?: number | null;
  consumo_fora_ponta_anual?: number | null;
  consumo_efetivo_anual?: number | null;
  consumo_efetivo_medio?: number | null;
  despesa_ponta_anual?: number | null;
  despesa_fora_ponta_anual?: number | null;
  despesa_demanda_anual?: number | null;
}

const TIPOS_FATURA = [
  { value: 'Energia', label: 'Conta de Energia', icon: Zap },
  { value: 'Outros', label: 'Outros', icon: FileText },
];

export default function FaturasConsumo({ leadData, onUpdate }: FaturasConsumoProps) {
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faturaToDelete, setFaturaToDelete] = useState<Fatura | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [tipoDocumento, setTipoDocumento] = useState('Energia');
  const [descricao, setDescricao] = useState('');
  const [consumoData, setConsumoData] = useState<ConsumoData | null>(null);
  const [loadingConsumo, setLoadingConsumo] = useState(true);
  const { toast } = useToast();

  const fetchFaturas = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/cliente-faturas/${leadData.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar faturas');
      }

      const data = await response.json();
      setFaturas(data.faturas || []);
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as faturas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchConsumoData = async () => {
    try {
      setLoadingConsumo(true);
      
      // Determinar a tabela baseada no grupo tarifário do lead
      const grupoTarifario = leadData.grupo_tarifario || leadData.grupo;
      let tableName: string;
      let selectFields: string;

      if (grupoTarifario === 'Grupo B') {
        tableName = 'clientes_grupob';
        selectFields = 'consumo_anual, despesa_energia_anual, despesa_cosip_anual, despesa_fatura_anual';
      } else if (grupoTarifario === 'Grupo A') {
        tableName = 'clientes_grupoa';
        selectFields = 'consumo_ponta_anual, consumo_fora_ponta_anual, consumo_efetivo_anual, consumo_efetivo_medio, despesa_ponta_anual, despesa_fora_ponta_anual, despesa_demanda_anual, despesa_fatura_anual';
      } else if (grupoTarifario === 'Mercado Livre') {
        tableName = 'clientes_ml';
        selectFields = 'consumo_ponta_anual, consumo_fora_ponta_anual, consumo_efetivo_anual, consumo_efetivo_medio, despesa_ponta_anual, despesa_fora_ponta_anual, despesa_demanda_anual, despesa_fatura_anual';
      } else {
        // Tentar buscar de todas as tabelas se o grupo não estiver definido
        console.warn('Grupo tarifário não definido, tentando buscar de todas as tabelas');
        
        // Tentar Grupo B primeiro
        const { data: dataGrupoB, error: errorGrupoB } = await supabase
          .from('clientes_grupob')
          .select('consumo_anual, despesa_energia_anual, despesa_cosip_anual, despesa_fatura_anual')
          .eq('lead_id', leadData.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!errorGrupoB && dataGrupoB) {
          setConsumoData(dataGrupoB);
          return;
        }

        // Tentar Grupo A
        const { data: dataGrupoA, error: errorGrupoA } = await supabase
          .from('clientes_grupoa')
          .select('consumo_ponta_anual, consumo_fora_ponta_anual, consumo_efetivo_anual, consumo_efetivo_medio, despesa_ponta_anual, despesa_fora_ponta_anual, despesa_demanda_anual, despesa_fatura_anual')
          .eq('lead_id', leadData.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!errorGrupoA && dataGrupoA) {
          setConsumoData(dataGrupoA);
          return;
        }

        // Tentar Mercado Livre
        const { data: dataML, error: errorML } = await supabase
          .from('clientes_ml')
          .select('consumo_ponta_anual, consumo_fora_ponta_anual, consumo_efetivo_anual, consumo_efetivo_medio, despesa_ponta_anual, despesa_fora_ponta_anual, despesa_demanda_anual, despesa_fatura_anual')
          .eq('lead_id', leadData.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!errorML && dataML) {
          setConsumoData(dataML);
          return;
        }

        setConsumoData(null);
        return;
      }

      const { data, error } = await supabase
        .from(tableName)
        .select(selectFields)
        .eq('lead_id', leadData.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar dados de consumo:', error);
        setConsumoData(null);
      } else {
        setConsumoData(data);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de consumo:', error);
      setConsumoData(null);
    } finally {
      setLoadingConsumo(false);
    }
  };

  useEffect(() => {
    if (leadData.id) {
      fetchFaturas();
      fetchConsumoData();
    }
  }, [leadData.id]);

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Selecione pelo menos um arquivo',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const formData = new FormData();
      Array.from(selectedFiles).forEach(file => {
        formData.append('faturas', file);
      });
      formData.append('leadId', leadData.id);
      formData.append('tipoDocumento', tipoDocumento);
      if (descricao) {
        formData.append('descricao', descricao);
      }

      const response = await fetch('/api/cliente-faturas/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao fazer upload');
      }

      toast({
        title: 'Sucesso',
        description: 'Fatura(s) enviada(s) com sucesso',
      });

      // Limpar formulário
      setSelectedFiles(null);
      setTipoDocumento('Energia');
      setDescricao('');
      setUploadDialogOpen(false);
      
      // Atualizar lista
      fetchFaturas();
      onUpdate();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao fazer upload',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fatura: Fatura) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/cliente-faturas/download/${fatura.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao gerar link de download');
      }

      const data = await response.json();
      
      // Abrir URL em nova aba
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível fazer o download da fatura',
        variant: 'destructive',
      });
    }
  };

  const handleView = async (fatura: Fatura) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/cliente-faturas/download/${fatura.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao gerar link de visualização');
      }

      const data = await response.json();
      
      // Abrir URL em nova aba para visualização
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Erro ao visualizar fatura:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível visualizar a fatura',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!faturaToDelete) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`/api/cliente-faturas/${faturaToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar fatura');
      }

      toast({
        title: 'Sucesso',
        description: 'Fatura deletada com sucesso',
      });

      setDeleteDialogOpen(false);
      setFaturaToDelete(null);
      fetchFaturas();
      onUpdate();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível deletar a fatura',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileIcon = (tipoArquivo: string) => {
    if (tipoArquivo.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (tipoArquivo.includes('image')) return <Image className="h-5 w-5 text-blue-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getTipoIcon = (tipo: string) => {
    const tipoData = TIPOS_FATURA.find(t => t.value === tipo);
    if (!tipoData) return <FileText className="h-4 w-4" />;
    const Icon = tipoData.icon;
    return <Icon className="h-4 w-4" />;
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined, unit: string = '') => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value) + (unit ? ` ${unit}` : '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Faturas - Ocupa 2 colunas */}
      <div className="lg:col-span-2 space-y-6">
        {/* Header com botão de upload */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Faturas de Consumo</h3>
            <p className="text-sm text-muted-foreground">
              Gerencie as faturas do cliente
            </p>
          </div>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Enviar Fatura
          </Button>
        </div>

        {/* Lista de faturas */}
            {faturas.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma fatura encontrada</h3>
              <p className="text-muted-foreground text-center max-w-md mb-4">
                Este cliente ainda não possui faturas cadastradas. Clique no botão acima para enviar faturas.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
            {faturas.map((fatura) => (
              <Card key={fatura.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getFileIcon(fatura.tipo_arquivo)}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm whitespace-normal break-words">
                          {fatura.nome_original}
                        </CardTitle>
                        <CardDescription className="text-xs flex items-center gap-1 mt-1">
                          {getTipoIcon(fatura.tipo_documento)}
                          <span>{fatura.tipo_documento}</span>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Tamanho: {formatFileSize(fatura.tamanho_bytes)}</p>
                    <p>Enviado em: {formatDate(fatura.created_at)}</p>
                    {fatura.descricao && (
                      <p className="italic">"{fatura.descricao}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(fatura)}
                      className="flex-1"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Baixar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(fatura)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFaturaToDelete(fatura);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Resumo de Consumo - Lado Direito */}
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumo de Consumo Mensal</CardTitle>
            <CardDescription>
              Dados calculados a partir do dimensionamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingConsumo ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : consumoData ? (
              (() => {
                // Determinar o grupo baseado nos dados retornados ou no leadData
                const grupoTarifario = leadData.grupo_tarifario || leadData.grupo;
                // Se o grupo não estiver definido, tentar inferir dos dados
                const isGrupoB = grupoTarifario === 'Grupo B' || 
                                 (grupoTarifario !== 'Grupo A' && grupoTarifario !== 'Mercado Livre' && 
                                  consumoData.consumo_anual !== undefined && consumoData.consumo_anual !== null);
                
                return (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {isGrupoB ? (
                        <>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Consumo Mensal</span>
                            <span className="text-sm font-semibold">
                              {formatNumber(consumoData.consumo_anual ? consumoData.consumo_anual / 12 : null, 'kWh')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Despesa Energia</span>
                            <span className="text-sm font-semibold">
                              {formatCurrency(consumoData.despesa_energia_anual ? consumoData.despesa_energia_anual / 12 : null)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Despesa COSIP</span>
                            <span className="text-sm font-semibold">
                              {formatCurrency(consumoData.despesa_cosip_anual ? consumoData.despesa_cosip_anual / 12 : null)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Despesa Total Fatura</span>
                            <span className="text-sm font-semibold text-primary">
                              {formatCurrency(consumoData.despesa_fatura_anual ? consumoData.despesa_fatura_anual / 12 : null)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Consumo Efetivo Médio</span>
                            <span className="text-sm font-semibold">
                              {formatNumber(consumoData.consumo_efetivo_medio || (consumoData.consumo_efetivo_anual ? consumoData.consumo_efetivo_anual / 12 : null), 'kWh')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Consumo Ponta Mensal</span>
                            <span className="text-sm font-semibold">
                              {formatNumber(consumoData.consumo_ponta_anual ? consumoData.consumo_ponta_anual / 12 : null, 'kWh')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Consumo Fora Ponta Mensal</span>
                            <span className="text-sm font-semibold">
                              {formatNumber(consumoData.consumo_fora_ponta_anual ? consumoData.consumo_fora_ponta_anual / 12 : null, 'kWh')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Despesa Ponta</span>
                            <span className="text-sm font-semibold">
                              {formatCurrency(consumoData.despesa_ponta_anual ? consumoData.despesa_ponta_anual / 12 : null)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Despesa Fora Ponta</span>
                            <span className="text-sm font-semibold">
                              {formatCurrency(consumoData.despesa_fora_ponta_anual ? consumoData.despesa_fora_ponta_anual / 12 : null)}
                            </span>
                          </div>
                          {consumoData.despesa_demanda_anual && (
                            <div className="flex items-center justify-between py-2 border-b">
                              <span className="text-sm font-medium text-muted-foreground">Despesa Demanda</span>
                              <span className="text-sm font-semibold">
                                {formatCurrency(consumoData.despesa_demanda_anual / 12)}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between py-2 border-b">
                            <span className="text-sm font-medium text-muted-foreground">Despesa Total Fatura</span>
                            <span className="text-sm font-semibold text-primary">
                              {formatCurrency(consumoData.despesa_fatura_anual ? consumoData.despesa_fatura_anual / 12 : null)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="pt-2 text-xs text-muted-foreground italic">
                      * Valores mensais calculados a partir dos dados anuais do dimensionamento
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  Dados de consumo não disponíveis
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Complete o dimensionamento para visualizar o resumo
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Upload */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar Fatura</DialogTitle>
            <DialogDescription>
              Selecione um ou mais arquivos para enviar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo de Fatura</Label>
              <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                <SelectTrigger id="tipo">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_FATURA.map((tipo) => {
                    const Icon = tipo.icon;
                    return (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input
                id="descricao"
                placeholder="Ex: Fatura de Janeiro/2024"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arquivos">Arquivos</Label>
              <Input
                id="arquivos"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setSelectedFiles(e.target.files)}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="arquivos"
                  className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary font-medium py-2 px-4 rounded-md inline-block transition-colors"
                >
                  Escolher Arquivos
                </Label>
                <div className="text-sm text-muted-foreground min-w-0 flex-1">
                  {selectedFiles && selectedFiles.length > 0 ? (
                    <span className="block whitespace-normal break-words">
                      {selectedFiles.length === 1
                        ? selectedFiles[0].name
                        : `${selectedFiles.length} arquivos selecionados`}
                    </span>
                  ) : (
                    <span className="block whitespace-normal break-words">Nenhum arquivo selecionado</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB por arquivo)
              </p>
            </div>
            {selectedFiles && selectedFiles.length > 0 && (
              <div className="bg-muted p-3 rounded-md min-w-0">
                <p className="text-sm font-medium mb-2">
                  {selectedFiles.length} arquivo(s) selecionado(s):
                </p>
                <ul className="text-xs space-y-1">
                  {Array.from(selectedFiles).map((file, index) => (
                    <li key={index} className="whitespace-normal break-words">
                      • {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFiles(null);
                setDescricao('');
              }}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFiles}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a fatura "{faturaToDelete?.nome_original}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFaturaToDelete(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
